import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import productInquiryData from "../../data/productInquiryData";
import { API_BASE } from "../../config";
import "./ProductInquiryPanel.css";

/* ============================================================
   PRODUCT INQUIRY PANEL
   Auto-opens from the right, 3 seconds after landing on the
   Home page. Shows only once per browser tab session. A manual
   "Get Quote" button (see GetQuoteButton.jsx) can reopen it from
   any page, any time.

   On submit, the inquiry is:
   1. Saved to the database via the same Enquiry API your
      /enquiry page already uses (shows up in Admin > Enquiries)
   2. Also sent as a formatted WhatsApp message to your business
      number, so you get an instant notification too.
   ============================================================ */

const WHATSAPP_NUMBER = "919879380528";
const AUTO_OPEN_DELAY_MS = 3000;
const SESSION_KEY = "savariya_inquiry_panel_shown";

const STEPS = ["industry", "category", "brandSize", "quantity", "customer", "done"];

const ProductInquiryPanel = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const [industry, setIndustry] = useState("");
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [size, setSize] = useState("");
  const [core, setCore] = useState("");
  const [coils, setCoils] = useState("");
  const [meters, setMeters] = useState("");

  const [customer, setCustomer] = useState({
    name: "",
    company: "",
    mobile: "",
    email: "",
    gst: "",
    state: "",
    city: "",
    message: "",
  });

  useEffect(() => {
    const alreadyShown = sessionStorage.getItem(SESSION_KEY);
    const isHomePage = location.pathname === "/";

    let timer;
    if (!alreadyShown && isHomePage) {
      timer = setTimeout(() => {
        setIsOpen(true);
        sessionStorage.setItem(SESSION_KEY, "true");
      }, AUTO_OPEN_DELAY_MS);
    }

    // Manual open — triggered by the "Get Quote" button, works even
    // after the auto-popup has already shown once this session.
    const handleManualOpen = () => {
      setStepIndex(0);
      setIsOpen(true);
    };
    window.addEventListener("open-inquiry-panel", handleManualOpen);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("open-inquiry-panel", handleManualOpen);
    };
  }, [location.pathname]);

  const step = STEPS[stepIndex];

  const goNext = () => setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));
  const resetAndClose = () => setIsOpen(false);

  const categoryData =
    industry && category ? productInquiryData[industry][category] : null;

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);

    const productLabel = `${category} — ${brand} — ${size}${
      core ? ` — ${core}` : ""
    } (${industry})`;
    const quantityLabel = `Coils: ${coils || "-"}, Meters: ${meters || "-"}`;

    // 1) Save to database via the existing Enquiry API
    try {
      await fetch(`${API_BASE}?module=enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: customer.name,
          company: customer.company,
          mobile: customer.mobile,
          email: customer.email,
          product: productLabel,
          quantity: quantityLabel,
          message: `${customer.message ? customer.message + " | " : ""}GST: ${
            customer.gst || "-"
          } | Location: ${customer.city}, ${customer.state}`,
        }),
      });
    } catch (err) {
      // Don't block the WhatsApp path if the backend is unreachable —
      // just note it so we can show a soft warning if needed.
      setSubmitError("db");
    }

    // 2) Also send an instant WhatsApp notification
    const lines = [
      "New product inquiry from website:",
      `Industry: ${industry}`,
      `Category: ${category}`,
      `Brand: ${brand}`,
      `Size: ${size}`,
      core ? `Core: ${core}` : null,
      `Coils: ${coils || "-"}`,
      `Meters: ${meters || "-"}`,
      "---",
      `Name: ${customer.name}`,
      `Company: ${customer.company || "-"}`,
      `Mobile: ${customer.mobile}`,
      `Email: ${customer.email || "-"}`,
      `GST: ${customer.gst || "-"}`,
      `Location: ${customer.city}, ${customer.state}`,
      customer.message ? `Message: ${customer.message}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    const isMobile =
      typeof navigator !== "undefined" &&
      /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    const url = isMobile
      ? `https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent(lines)}`
      : `https://web.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent(lines)}`;

    window.open(url, "_blank", "noopener,noreferrer");

    setSubmitting(false);
    goNext(); // move to "done" step
  };

  if (!isOpen) return null;

  return (
    <div className="pip-overlay" onClick={resetAndClose}>
      <div className="pip-panel" onClick={(e) => e.stopPropagation()}>
        <button className="pip-close" onClick={resetAndClose} aria-label="Close">
          ×
        </button>

        <div className="pip-header">
          <h3>Get a quick quote</h3>
          <p>Takes less than a minute</p>
        </div>

        {/* ---------- STEP: INDUSTRY ---------- */}
        {step === "industry" && (
          <div className="pip-body">
            <label className="pip-label">Select your industry</label>
            {Object.keys(productInquiryData).map((ind) => (
              <button
                key={ind}
                className={`pip-option ${industry === ind ? "active" : ""}`}
                onClick={() => {
                  setIndustry(ind);
                  setCategory("");
                  setBrand("");
                  setSize("");
                }}
              >
                {ind}
              </button>
            ))}
            <button className="pip-next" disabled={!industry} onClick={goNext}>
              Next
            </button>
          </div>
        )}

        {/* ---------- STEP: CATEGORY ---------- */}
        {step === "category" && (
          <div className="pip-body">
            <label className="pip-label">Select category</label>
            {Object.keys(productInquiryData[industry]).map((cat) => (
              <button
                key={cat}
                className={`pip-option ${category === cat ? "active" : ""}`}
                onClick={() => {
                  setCategory(cat);
                  setBrand("");
                  setSize("");
                }}
              >
                {cat}
              </button>
            ))}
            <div className="pip-row">
              <button className="pip-back" onClick={goBack}>
                Back
              </button>
              <button className="pip-next" disabled={!category} onClick={goNext}>
                Next
              </button>
            </div>
          </div>
        )}

        {/* ---------- STEP: BRAND + SIZE ---------- */}
        {step === "brandSize" && categoryData && (
          <div className="pip-body">
            <label className="pip-label">Select brand</label>
            <select
              className="pip-select"
              value={brand}
              onChange={(e) => {
                setBrand(e.target.value);
                setSize("");
              }}
            >
              <option value="">Choose brand</option>
              {Object.keys(categoryData.brands).map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>

            {brand && (
              <>
                <label className="pip-label">Select size</label>
                <div className="pip-chip-wrap">
                  {categoryData.brands[brand].map((s) => (
                    <button
                      key={s}
                      className={`pip-chip ${size === s ? "active" : ""}`}
                      onClick={() => setSize(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </>
            )}

            {categoryData.cores && (
              <>
                <label className="pip-label">Select core</label>
                <div className="pip-chip-wrap">
                  {categoryData.cores.map((c) => (
                    <button
                      key={c}
                      className={`pip-chip ${core === c ? "active" : ""}`}
                      onClick={() => setCore(c)}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="pip-row">
              <button className="pip-back" onClick={goBack}>
                Back
              </button>
              <button className="pip-next" disabled={!brand || !size} onClick={goNext}>
                Next
              </button>
            </div>
          </div>
        )}

        {/* ---------- STEP: QUANTITY ---------- */}
        {step === "quantity" && (
          <div className="pip-body">
            <label className="pip-label">Coils</label>
            <input
              className="pip-input"
              type="number"
              min="0"
              placeholder="Number of coils"
              value={coils}
              onChange={(e) => setCoils(e.target.value)}
            />
            <label className="pip-label">Meters</label>
            <input
              className="pip-input"
              type="number"
              min="0"
              placeholder="Total meters"
              value={meters}
              onChange={(e) => setMeters(e.target.value)}
            />
            <div className="pip-row">
              <button className="pip-back" onClick={goBack}>
                Back
              </button>
              <button
                className="pip-next"
                disabled={!coils && !meters}
                onClick={goNext}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* ---------- STEP: CUSTOMER INFO ---------- */}
        {step === "customer" && (
          <div className="pip-body">
            <input
              className="pip-input"
              placeholder="Name *"
              value={customer.name}
              onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
            />
            <input
              className="pip-input"
              placeholder="Company name *"
              value={customer.company}
              onChange={(e) => setCustomer({ ...customer, company: e.target.value })}
            />
            <input
              className="pip-input"
              placeholder="Mobile number *"
              value={customer.mobile}
              onChange={(e) => setCustomer({ ...customer, mobile: e.target.value })}
            />
            <input
              className="pip-input"
              placeholder="Email"
              value={customer.email}
              onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
            />
            <input
              className="pip-input"
              placeholder="GST No. (optional)"
              value={customer.gst}
              onChange={(e) => setCustomer({ ...customer, gst: e.target.value })}
            />
            <div className="pip-row">
              <input
                className="pip-input"
                placeholder="State *"
                value={customer.state}
                onChange={(e) => setCustomer({ ...customer, state: e.target.value })}
              />
              <input
                className="pip-input"
                placeholder="City *"
                value={customer.city}
                onChange={(e) => setCustomer({ ...customer, city: e.target.value })}
              />
            </div>
            <textarea
              className="pip-input pip-textarea"
              placeholder="Requirement / message"
              value={customer.message}
              onChange={(e) => setCustomer({ ...customer, message: e.target.value })}
            />
            <div className="pip-row">
              <button className="pip-back" onClick={goBack}>
                Back
              </button>
              <button
                className="pip-next"
                disabled={
                  submitting ||
                  !customer.name ||
                  !customer.company ||
                  !customer.mobile ||
                  !customer.state ||
                  !customer.city
                }
                onClick={handleSubmit}
              >
                {submitting ? "Submitting..." : "Submit inquiry"}
              </button>
            </div>
          </div>
        )}

        {/* ---------- STEP: DONE ---------- */}
        {step === "done" && (
          <div className="pip-body pip-done">
            <div className="pip-done-icon">✓</div>
            <h4>Inquiry sent</h4>
            <p>
              Your details have been saved and sent on WhatsApp. Our sales
              team will follow up with a quotation shortly.
            </p>
            {submitError === "db" && (
              <p className="pip-warning">
                (Note: we couldn't reach the server just now, but your
                WhatsApp message went through.)
              </p>
            )}
            <button className="pip-next" onClick={resetAndClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductInquiryPanel;
